import { Link } from 'react-router-dom';
import { ExternalLink, Smartphone } from 'lucide-react';
import { GooglePlayIcon, PLAY_STORE_URL, UTILOOM_URL } from '@/components/CalcNotePromo';

export function Footer() {
  return (
    <footer className="border-t border-border bg-muted/30 mt-12">
      <div className="container mx-auto px-4 sm:px-6 py-10 sm:py-14 max-w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-10">
          
          {/* Brand - Section 1 */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-black tracking-tight text-foreground">
                Calculator<span className="text-primary">Sheet</span>
              </span>
            </div>
            <p className="text-muted-foreground text-sm max-w-md leading-relaxed">
              CalculatorSheet is your free universal calculator platform — bringing finance, investment, vehicle, utility, and health calculators into one fast and reliable experience.
            </p>

            <div className="pt-2">
              <p className="text-xs font-medium text-muted-foreground">
                Developed &amp; Maintained by{' '}
                <a
                  href={UTILOOM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold text-foreground hover:text-primary transition-colors underline decoration-primary/50 underline-offset-4 inline-flex items-center gap-1"
                >
                  Utiloom (utiloom.in)
                  <ExternalLink className="w-3 h-3" />
                </a>
              </p>
            </div>
          </div>

          {/* Calculators - Section 2 */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wider text-foreground mb-4">Calculators</h4>
            <ul className="space-y-2.5 text-sm text-muted-foreground">
              <li>
                <Link to="/" className="hover:text-primary transition-colors">
                  EMI Calculator
                </Link>
              </li>
              <li>
                <Link to="/sip-calculator" className="hover:text-primary transition-colors">
                  SIP Calculator
                </Link>
              </li>
              <li>
                <Link to="/fd-calculator" className="hover:text-primary transition-colors">
                  FD Calculator
                </Link>
              </li>
              <li>
                <Link to="/compound-interest-calculator" className="hover:text-primary transition-colors">
                  Compound Interest
                </Link>
              </li>
              <li>
                <Link to="/gst-calculator" className="hover:text-primary transition-colors">
                  GST Calculator
                </Link>
              </li>
              <li>
                <Link to="/percentage-calculator" className="hover:text-primary transition-colors">
                  Percentage Calculator
                </Link>
              </li>
              <li>
                <Link to="/mileage-calculator" className="hover:text-primary transition-colors">
                  Mileage Calculator
                </Link>
              </li>
              <li>
                <Link to="/calculator" className="hover:text-primary transition-colors">
                  General &amp; Scientific
                </Link>
              </li>
              <li>
                <Link to="/age-calculator" className="hover:text-primary transition-colors">
                  Age Calculator
                </Link>
              </li>
              <li>
                <Link to="/bmi-calculator" className="hover:text-primary transition-colors">
                  BMI Calculator
                </Link>
              </li>
            </ul>
          </div>

          {/* Mobile App Promotion - Section 3 */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wider text-foreground mb-4 flex items-center gap-1.5">
              <Smartphone className="w-4 h-4 text-primary" />
              <span>Mobile App</span>
            </h4>
            <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
              Get <strong className="text-foreground">CalcNote</strong> on Android. Smart calculations + note taking on the go.
            </p>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-foreground text-background px-3.5 py-2 text-xs font-semibold shadow hover:bg-foreground/90 transition-all hover:scale-105"
            >
              <GooglePlayIcon className="w-4 h-4" />
              <span>Get on Google Play</span>
            </a>
            <p className="text-[11px] text-muted-foreground mt-2">
              Publisher: <strong className="text-foreground">Utiloom</strong>
            </p>
          </div>

          {/* Legal & Company - Section 4 */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wider text-foreground mb-4">About &amp; Trust</h4>
            <ul className="space-y-2.5 text-sm text-muted-foreground">
              <li>
                <Link to="/about" className="hover:text-primary transition-colors font-medium text-foreground">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-primary transition-colors font-medium text-foreground">
                  Contact &amp; Support
                </Link>
              </li>
              <li>
                <Link to="/privacy-policy" className="hover:text-primary transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms-conditions" className="hover:text-primary transition-colors">
                  Terms &amp; Conditions
                </Link>
              </li>
              <li>
                <Link to="/disclaimer" className="hover:text-primary transition-colors">
                  Disclaimer
                </Link>
              </li>
              <li>
                <a
                  href={UTILOOM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary transition-colors inline-flex items-center gap-1 font-medium text-muted-foreground hover:text-foreground"
                >
                  Utiloom (utiloom.in)
                  <ExternalLink className="w-3 h-3 text-muted-foreground" />
                </a>
              </li>
            </ul>
          </div>

        </div>

        {/* Bottom Strip */}
        <div className="border-t border-border/80 mt-10 pt-6 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-muted-foreground">
          <p className="flex items-center gap-1 flex-wrap text-center md:text-left">
            <span>© {new Date().getFullYear()} CalculatorSheet. All rights reserved.</span>
            <span>•</span>
            <span>
              Developed and maintained by{' '}
              <a
                href={UTILOOM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-foreground hover:text-primary transition-colors underline decoration-dotted"
              >
                Utiloom
              </a>
            </span>
          </p>

          <div className="flex items-center gap-4">
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1"
            >
              <GooglePlayIcon className="w-3.5 h-3.5" />
              <span>CalcNote on Play Store</span>
            </a>
            <span className="text-border">|</span>
            <a
              href={UTILOOM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary transition-colors"
            >
              utiloom.in
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}